// Protocol scheduling — pure functions shared by the subcontractor timeline,
// the alarm manager and (in mirrored form) the protocol-dispatcher edge
// function. No supabase imports: everything here is unit-testable maths.
//
// Anchor rules (must match the dispatcher exactly):
//   clock       → fires at anchor_time on the given date.
//   route_start → fires at route start + offset_minutes (negative = earlier);
//                 unresolvable when the day's route start is unknown.
//   after_prev  → never has a fire time; it unlocks when the previous step is
//                 completed, and is never scheduled or pushed.
// Only push/alarm steps are ever dispatched; silent steps still get a fireAt
// so the timeline can order and annotate them.

// Same regex the route-handling SQL uses on dropbox_route_files.extracted_text.
export const ROUTE_START_REGEX = /Suggested start time:\s*(\d{2}:\d{2})/i;

// 'HH:MM' from a route file's extracted text, or null when no time is printed.
export function parseRouteStartTime(text) {
  const match = String(text || '').match(ROUTE_START_REGEX);
  return match ? match[1] : null;
}

// 'HH:MM' (or 'HH:MM:SS' from a time column) → minutes since midnight, or null.
export function parseHHMM(value) {
  const match = String(value || '').match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

// Local-time Date for `date` ('YYYY-MM-DD') at `minutes` since midnight.
// Built from parts (not ISO parsing) so it is the device's local wall clock —
// crews and depots run on the same UK local time as the device.
function dateAtMinutes(date, minutes) {
  const [y, mo, d] = String(date).split('-').map(Number);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d, Math.floor(minutes / 60), minutes % 60, 0, 0);
}

// steps → [{ step, fireAt: Date|null, scheduled: bool }], ordered by position.
//   fireAt    — when the step lands on the timeline (null = unresolvable /
//               sequence-anchored).
//   scheduled — whether the dispatcher would push it (fire time resolved AND
//               alert_level is push/alarm).
export function resolveStepTimes(steps, { routeStartHHMM, date } = {}) {
  const routeStartMin = parseHHMM(routeStartHHMM);
  const ordered = [...(steps || [])].sort((a, b) => (a.position || 0) - (b.position || 0));

  return ordered.map((step) => {
    let fireMin = null;
    if (step.anchor_type === 'clock') {
      fireMin = parseHHMM(step.anchor_time);
    } else if (step.anchor_type === 'route_start' && routeStartMin != null) {
      fireMin = routeStartMin + Number(step.offset_minutes || 0);
    }

    const fireAt = fireMin != null && date ? dateAtMinutes(date, fireMin) : null;
    const scheduled = fireAt != null && (step.alert_level === 'push' || step.alert_level === 'alarm');
    return { step, fireAt, scheduled };
  });
}

// Does a protocol's audience include this subcontractor? Guidance-only UX
// filter — missing/'all' audiences match everyone; a scoped audience matches
// only when the relevant id is listed.
export function audienceMatches(audience, { subId, crewId, depotId } = {}) {
  if (!audience || !audience.scope || audience.scope === 'all') return true;
  const ids = Array.isArray(audience.ids) ? audience.ids : [];
  if (audience.scope === 'depots') return depotId != null && ids.includes(depotId);
  if (audience.scope === 'crews') return crewId != null && ids.includes(crewId);
  if (audience.scope === 'subs') return subId != null && ids.includes(subId);
  return false; // unknown scope: don't suggest it to anyone
}
